/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { makeDefaultFilter } from 'api/Filter';

import { ia, iaRa } from 'lib/helpers/utility';
import usePortalNavigate from 'lib/hooks/usePortalNavigate';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';
import { useOperatingSystem } from 'lib/hooks/useOperatingSystem';

import { checkActiveItem } from '../lib/checkActiveItem';
import TooltipContent from '../components/TooltipContent';

export function useViewTabs({
  title,
  items,
  category,
  updateFilters,
  resetFilters,
  removeDefaults = false,
  openModal,
  openEditModal,
  counts,
  allLabel,
  // Views saved on another table of the same category are left out.
  type
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view');
  const os = useOperatingSystem();

  const navigate = useNavigate();
  // Switch-item targets are absolute /portal/... paths; rewrite to the admin
  // viewport base so a tab click doesn't bounce an admin to /login.
  const portalNavigate = usePortalNavigate();
  const location = useLocation();

  const activeItem = useMemo(() => checkActiveItem(items, view, location), [items, location, view]);

  const onSwitchItemClick = (item) => {
    if (item.navigate) {
      portalNavigate(item.navigate, {
        state: { disableDefaultFilter: true }
      });
      resetFilters();
      return;
    }

    const newParams = new URLSearchParams(searchParams);

    if (item?.value === null) {
      newParams.delete('view');
      resetFilters();
    } else {
      newParams.set('view', item.value);
    }

    setSearchParams(newParams);

    navigate(location.pathname + '?' + newParams.toString(), {
      state: { disableDefaultFilter: true }
    });
  };

  const { data: filtersData } = useFilters({
    params: { category },
    dependencies: [category]
  });
  const savedViews = iaRa(filtersData?.filters).filter(
    (filter) => !type || !filter.type || filter.type === type
  );

  const onFilterClick = (filter) => {
    setSearchParams((prev) => {
      if (filter.name === null) {
        prev.delete('view');
        updateFilters(null);

        return prev;
      }
      const selectedFilter = iaRa(filtersData?.filters).find(
        (f) => f.name === filter.name && f.category === category
      );

      prev.set('view', filter.name);

      if (!selectedFilter) return prev;

      updateFilters(selectedFilter);
      return prev;
    });
  };

  const { mutate: makeDefaultFilterHandler, isLoading: isMakingDefaultFilter } = useCustomMutation({
    mutationFn: (params) => makeDefaultFilter(navigate, params),
    successMessage: 'Filter set as default',
    errorMessage: "Couldn't set filter as default. Please try again.",
    invalidateQueryKey: ['default-filters', ['filters', category]]
  });

  const ordered = allLabel
    ? [
        ...iaRa(items)
          .filter((item) => item.isAllTab)
          .map((item) => ({ ...item, label: allLabel })),
        ...iaRa(items).filter((item) => !item.isAllTab)
      ]
    : items;
  const hasNavigate = iaRa(ordered).some(
    (item) => (!!item.navigate && 'type' in item) || !item.value || item.isAllTab
  );

  const filtered = [
    ...(!hasNavigate
      ? [
          {
            label: allLabel || `All ${title || category}`,
            value: null
          }
        ]
      : []),
    ...iaRa(ordered)
  ];
  const removeItems = removeDefaults && !ia(savedViews) ? [] : filtered;

  const toggleItems = useMemo(() => {
    let firstActionAdded = false;
    return iaRa(removeItems)
      .map((item) => ({
        ...item,
        // Built-in views are counted when the table reports counts, so an empty one reads 0;
        // saved views are not counted and show none.
        count: counts ? (counts[item.value ?? 'all'] ?? 0) : item.count,
        onClick: () => onSwitchItemClick(item)
      }))
      .concat(
        savedViews.map((filter) => {
          const item = {
            label: filter.name,
            value: filter.name,
            default: filter.is_default,
            onClick: () => onFilterClick(filter),
            className: 'px-2 hover:bg-primary-50',
            actions: [
              {
                label: 'Rename view',
                value: 'edit',
                icon: 'editor-firstline3rec-mono',
                onClick: () => openEditModal(filter),
                textClassName: 'text-neutral-900'
              },
              {
                label: filter.is_default ? 'Unset as default view' : 'Set as default view',
                value: 'edit',
                icon: 'star-1-mono',
                onClick: () => {
                  if (isMakingDefaultFilter) return;
                  makeDefaultFilterHandler({
                    id: filter.id,
                    category,
                    isDefault: !filter.is_default
                  });
                },
                textClassName: 'text-neutral-900'
              },
              {
                label: 'Delete',
                value: 'delete',
                icon: 'trash-mono',
                onClick: () => openModal({ id: filter.id, name: filter.name }),
                textClassName: 'text-neutral-900'
              }
            ]
          };
          if (!firstActionAdded && item.actions) {
            item.split = true;
            firstActionAdded = true;
          }
          return item;
        })
      )
      .map((item, index) => ({
        ...item,
        tooltip: <TooltipContent label={item.label} index={index} os={os} />
      }));
  }, [filtersData, type, items, category, location, counts, allLabel]);

  return {
    view,
    activeItem,
    filtersData,
    onSwitchItemClick,
    onFilterClick,
    makeDefaultFilterHandler,
    isMakingDefaultFilter,
    removeItems,
    toggleItems
  };
}
